import React, { FC } from 'react';
import type { Symptom } from '@packages/domain/cvt';
import { createUseStyles } from 'react-jss';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@web/components/table';
import { Typography, TextBadge } from '@packages/ui/shared';
import { upperCase } from 'lodash';
export interface CvtSymptomsTableColumn {
    getValue: (symptom: Symptom, index: number) => string;
    key: string;
    label: string;
}

export const CVT_SYMPTOMS_TABLE_COLUMNS: CvtSymptomsTableColumn[] = [
    {
        key: 'no',
        label: 'NO',
        getValue: (_, index) => String(index + 1),
    },
    {
        key: 'title',
        label: 'Diagnosed Symptoms',
        getValue: (symptom) => upperCase(symptom.title),
    },
    {
        key: 'description',
        label: 'Details of symptoms',
        getValue: (symptom) => symptom.description,
    },
];

export const getSymptomsTablePdfColumns = (): string[] => CVT_SYMPTOMS_TABLE_COLUMNS.map((column) => column.label);

export const getSymptomsTablePdfData = (symptoms: Symptom[]): string[][] =>
    symptoms.map((symptom, index) => CVT_SYMPTOMS_TABLE_COLUMNS.map((column) => column.getValue(symptom, index)));

const useStyles = createUseStyles({
    recommendationContainer: {
        marginTop: 24,
    },
    recommendationWrapper: {
        border: '1px solid #E0E0E0',
        marginTop: 24,
        padding: '16px',
    },
    recommendationContent: {
        paddingTop: 8,
        whiteSpace: 'normal',
        lineHeight: 1.5,
    },
});

type CvtReportTableProps = {
    repairRecommendation?: string[];
    symptoms?: Symptom[];
};

export const CvtReportTable: FC<CvtReportTableProps> = ({ symptoms, repairRecommendation }) => {
    const classes = useStyles();
    const hasRepairRecommendation = repairRecommendation && repairRecommendation.length > 0;

    if (!symptoms || symptoms.length === 0) {
        return null;
    }

    return (
        <>
            <Table>
                <TableHead>
                    <TableRow>
                        {CVT_SYMPTOMS_TABLE_COLUMNS.map((col) => (
                            <TableHeader key={col.key}>
                                <Typography variant="label">{col.label}</Typography>
                            </TableHeader>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {symptoms?.map((symptom, idx) => (
                        <TableRow key={idx}>
                            <TableCell>
                                <Typography>{idx + 1}</Typography>
                            </TableCell>
                            <TableCell>
                                <TextBadge variant="yellow" style={{ marginLeft: 5 }}>
                                    {symptom.title}
                                </TextBadge>
                            </TableCell>
                            <TableCell>
                                <Typography>{symptom.description}</Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {hasRepairRecommendation && (
                <div className={classes.recommendationContainer}>
                    <Typography variant="h3">Repair Recommendation</Typography>
                    <div className={classes.recommendationWrapper}>
                        <ul className={classes.recommendationContent}>
                            {repairRecommendation.map((item, index) => (
                                <li key={index}>
                                    <Typography>{item}</Typography>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </>
    );
};
